import Link from 'next/link';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/shadcn/accordion';
import { Button } from '@/components/shadcn/button';
import * as Typography from '@/components/shadcn/typography';
import { ExternalUrls } from '@/utils/url.ts';
import Regions from '@/components/ui/privacy/regions/Regions';
import { faqItems } from '@/utils/privacy';

export default function Overview() {
  return (
    <div className="flex flex-col gap-12">
      <section>
        <Typography.H4>Image Collection</Typography.H4>
        <Typography.P>
          The Hivemapper Network is designed to protect people&apos;s privacy
          while collecting imagery to build and update a global map. Your
          personal information and location data are not the product being
          monetized. The Hivemapper Network is building a private mapping
          network that offers contributors anonymity.
        </Typography.P>
      </section>
      <section>
        <Typography.H4>What Is Collected?</Typography.H4>
        <Typography.P>
          The Hivemapper Dashcam is an open-source product that collects
          information in a privacy-centric manner. The primary pieces of data it
          collects includes:
        </Typography.P>
        <div className="mt-4 grid grid-cols-3 gap-4">
          <div>
            <Typography.H4>Imagery</Typography.H4>
            <Typography.P>
              Street-level imagery in 2K and 4K resolution
            </Typography.P>
          </div>
          <div>
            <Typography.H4>GNSS Data</Typography.H4>
            <Typography.P>
              Accurately positions map imagery and features
            </Typography.P>
          </div>
          <div>
            <Typography.H4>IMU Data</Typography.H4>
            <Typography.P>
              Inertial Measurement Unit sensor that measures acceleration
            </Typography.P>
          </div>
        </div>
        <Button className="mt-4" variant="default">
          <Link target="_blank" href={ExternalUrls.GithubODC}>
            Learn More
          </Link>
        </Button>
      </section>
      <section>
        <Typography.H4>How Imagery Data Is Used</Typography.H4>
        <Typography.P>
          We build Map APIs that businesses can consume.
        </Typography.P>
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div>
            <Typography.H4>Map Imagery API</Typography.H4>
            <Typography.P>
              Customers can access geolocated imagery with enhanced privacy.
            </Typography.P>
          </div>
          <div>
            <Typography.H4>Map Features API</Typography.H4>
            <Typography.P>
              Objects like speed limit signs, stop signs, traffic lights, and
              more.
            </Typography.P>
          </div>
        </div>
        <Button className="mt-4" variant="default">
          <Link href={ExternalUrls.Product}>Learn More</Link>
        </Button>
      </section>
      <section>
        <div className="mb-4">
          <Typography.H4>Privacy by Design</Typography.H4>
        </div>
        <Typography.H4>
          Randomly Generated Usernames + Location Data + Imagery collected
        </Typography.H4>
        <Typography.P>
          Each Contributor’s username is randomly generated to ensure anonymity,
          and contributors’ location data and imagery collected is never
          publicly tied to a username or Hivemapper profile.
        </Typography.P>
        <div className="mt-4">
          <Typography.H4>Privacy Blurring</Typography.H4>
        </div>
        <Typography.P>
          Automatically blurs faces, license plates, cars, and bodies from the
          imagery on the edge - dashcam mapping device - prior to uploading
          imagery.
        </Typography.P>
        <div className="mt-4">
          <Typography.H4>Edge Processing</Typography.H4>
        </div>
        <Typography.P>
          The Hivemapper Dashcam automatically privacy blurs images and detects
          objects (e.g., speed limit signs) on the edge.
        </Typography.P>
        <div className="mt-4">
          <Typography.H4>Privacy Zones</Typography.H4>
        </div>
        <Typography.P>
          Personal and network level privacy zones where all data collection is
          automatically paused.
        </Typography.P>
        <div className="mt-4">
          <Typography.H4>
            No Identifiable Info Collected from Contributors
          </Typography.H4>
        </div>
        <Typography.P>
          When contributors collect imagery, faces, license plates, cars, and
          bodies are automatically blurred on their Hivemapper Dashcams, to
          ensure the imagery we receive does not include personal information of
          road users (bystanders). Unblurred imagery is never shared with the
          Hivemapper App and Hivemapper has no access to unblurred imagery.
        </Typography.P>
        <div className="mt-4">
          <Typography.H4>
            No Personal Info Collected from Road Users (bystanders)
          </Typography.H4>
        </div>
        <Typography.P>
          Each Contributor’s username is randomly generated to ensure anonymity,
          and contributors’ location data and imagery collected is never
          publicly tied to a username or Hivemapper profile.
        </Typography.P>
        <div className="mt-4">
          <Typography.H4>Transparency</Typography.H4>
        </div>
        <Typography.P>
          In certain countries we require you to display a placard on your
          vehicle indicating that you are collecting for a map.{' '}
          <Link
            className="cursor-pointer underline"
            target="_blank"
            href={ExternalUrls.BeeLegalPlacards}>
            See here
          </Link>{' '}
          for the countries and placards.
        </Typography.P>
        <div className="mt-4">
          <Typography.H4>Open-Source Dashcam</Typography.H4>
        </div>
        <Typography.P>
          People can inspect the code and see what data is being collected and
          how it’s being collected,{' '}
          <Link
            className="cursor-pointer underline"
            target="_blank"
            href={ExternalUrls.GithubODC}>
            see here.
          </Link>
        </Typography.P>
        <Button className="mt-4" variant="default">
          <Link target="_blank" href={ExternalUrls.ProtectionAndPrivacy}>
            Learn More
          </Link>
        </Button>
      </section>
      <section>
        <Accordion type="single" collapsible className="w-full">
          {faqItems.map(question => (
            <AccordionItem key={question.title} value={question.title}>
              <AccordionTrigger>{question.title}</AccordionTrigger>
              <AccordionContent>
                <p dangerouslySetInnerHTML={{ __html: question.content }} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
      <section>
        <Typography.H4>Request Takedown</Typography.H4>
        <Typography.P>
          Hivemapper can assist with further blurring or deleting of imagery an
          individual considers sensitive or their personal information (e.g. a
          face, body, vehicle, license plate or home). If you would like to
          request this, please{' '}
          <Link
            className="cursor-pointer underline"
            href="/privacy/redaction-request">
            complete this form
          </Link>{' '}
          or send us a message to{' '}
          <Link
            className="cursor-pointer underline"
            href="mailto:privacy@hivemapper.com">
            privacy@hivemapper.com.
          </Link>
        </Typography.P>
      </section>
      <section>
        <Typography.H4>Active Imagery Collection</Typography.H4>
        <Typography.P>
          As of Q2 2024, imagery is being actively collected at these locations.
          You can also view map coverage{' '}
          <Link className="cursor-pointer underline" href="/network/coverage">
            here.
          </Link>
        </Typography.P>
        <Regions />
      </section>
    </div>
  );
}
